import {
	DataSourceSortableState,
	DataSourceSortDirection,
	convertSortDirectionToDto
} from "client/Data/sorting";
import {
	createRemoteDataSource,
	LazyLoadCursorKind
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";
import {
	AnnouncementPreviewItemModel,
	AnnouncementSortingInformationModel,
	AnnouncementPagingInformationModel,
	list,
	AnnouncementSortProperty
} from "client/Api/Hub/Announcements/announcementViewer";

export type AnnouncementFilterState = {
	categoryId: number | null;
	activeFilter: ActiveFilter | null;
	isPinned: boolean;
	isNewFilter: HubItemIsNewFilter;
};

export const useDataSource = createRemoteDataSource<
	AnnouncementPreviewItemModel,
	AnnouncementFilterState
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<AnnouncementPreviewItemModel>> => {
				const {
					after,
					amount,
					filterState,
					searchTerm,
					sorting,
					signal
				} = options;

				const {
					categoryId,
					activeFilter,
					isPinned,
					isNewFilter
				} = filterState;

				const [authorId, tagId] = createFilters(activeFilter);

				const sortOrder: AnnouncementSortingInformationModel | null = createOrder(sorting);

				const afterAnnouncement: AnnouncementPagingInformationModel | null = createPagingInformation(after);

				const response: LazyLoadPageResult<AnnouncementPreviewItemModel> = await list(
					{
						categoryId,
						authorId,
						tagId,
						searchTerm,
						isPinned,
						sortOrder,
						afterAnnouncement,
						amount,
						isNew: hubItemIsNewToFlag[isNewFilter]
					},
					signal
				);

				return response;
			}
		}
	);

export function createFilters(
	activeFilter: ActiveFilter | null
): [
	authorId: number | null,
	tagId: number | null
] {
	let authorId: number | null = null;
	let tagId: number | null = null;

	if (activeFilter) {
		switch (activeFilter.kind) {
			case "author":
				authorId = activeFilter.value
					.id;
				break;
			case "tag":
				tagId = activeFilter.value
					.id;
				break;
			default:
				never(activeFilter);
		}
	}

	return [authorId, tagId];
}

const propertyFilterMap: Partial<Record<PropertyPath<AnnouncementPreviewItemModel>, AnnouncementSortProperty>> = {
	["viewsCount"]: AnnouncementSortProperty.Views,
	["publicationStart"]: AnnouncementSortProperty.StartTime,
	["pinDate"]: AnnouncementSortProperty.PinDate
};

export function createOrder(
	sorting: DataSourceSortableState<AnnouncementPreviewItemModel> | undefined
): AnnouncementSortingInformationModel | null {
	let order: AnnouncementSortingInformationModel | null = null;

	if (sorting && sorting.direction !== DataSourceSortDirection.None) {
		const property: AnnouncementSortProperty | undefined = propertyFilterMap[sorting.propertyName];
		if (isNil(property)) {
			throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
		}

		order = {
			property,
			direction: convertSortDirectionToDto(sorting.direction)
		};
	}

	return order;
}

export function createPagingInformation(
	after: AnnouncementPreviewItemModel | undefined
): AnnouncementPagingInformationModel | null {
	let pagingInformation: AnnouncementPagingInformationModel | null = null;

	if (after) {
		pagingInformation = {
			id: after.id,
			startTime: after.publicationStart,
			viewsCount: after.viewsCount,
			pinDate: after.pinDate
		};
	}

	return pagingInformation;
}